import React, { useState, useEffect, useCallback } from 'react';
import { Edit2, Search, Package, X, Plus, Trash2, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HealthProfilesModule = ({ user }) => {
  const [profiles, setProfiles] = useState([]);
  const [allTests, setAllTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({});
  const [testSearch, setTestSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, tRes] = await Promise.all([
        axios.get(`${API}/admin/health-profiles`, { withCredentials: true }),
        axios.get(`${API}/admin/lab-tests`, { withCredentials: true }),
      ]);
      setProfiles(pRes.data);
      setAllTests(tRes.data);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = (p) => {
    setEditingId(p.profile_id);
    setForm({
      name: p.name, category: p.category, selling_price: p.selling_price || '',
      reference_mrp: p.reference_mrp || '', primary_use: p.primary_use || '',
      ideal_for: p.ideal_for || '', who_should_take: p.who_should_take || '',
      active: p.active !== false, b2b_cost: p.b2b_cost || '',
      internal_note: p.internal_note || '', test_codes: p.test_codes || [],
    });
    setExpandedId(p.profile_id);
  };

  const cancelEdit = () => { setEditingId(null); setForm({}); };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/admin/health-profiles/${editingId}`, form, { withCredentials: true });
      toast.success('Profile updated');
      setEditingId(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const addTest = (tc) => {
    if (!form.test_codes.includes(tc)) {
      setForm(p => ({ ...p, test_codes: [...p.test_codes, tc] }));
    }
  };

  const removeTest = (tc) => {
    setForm(p => ({ ...p, test_codes: p.test_codes.filter(c => c !== tc) }));
  };

  const filtered = profiles.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.profile_id?.toLowerCase().includes(search.toLowerCase()) ||
    p.category?.toLowerCase().includes(search.toLowerCase())
  );

  const categories = [...new Set(profiles.map(p => p.category).filter(Boolean))].sort();
  const activeCount = profiles.filter(p => p.active).length;

  const availableTests = allTests.filter(t =>
    t.active && (!testSearch || t.name.toLowerCase().includes(testSearch.toLowerCase()) || t.test_code.toLowerCase().includes(testSearch.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl p-3 shadow-sm text-center">
          <p className="text-2xl font-bold text-[#0A2540]">{profiles.length}</p>
          <p className="text-xs text-[#94A3B8]">Total Profiles</p>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-sm text-center">
          <p className="text-2xl font-bold text-green-600">{activeCount}</p>
          <p className="text-xs text-[#94A3B8]">Active</p>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-sm text-center">
          <p className="text-2xl font-bold text-sky-600">{categories.length}</p>
          <p className="text-xs text-[#94A3B8]">Categories</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search profiles by name, ID, or category..." className="pl-10 h-11 rounded-xl" data-testid="profile-search" />
      </div>

      {/* List */}
      {loading ? <p className="text-sm text-[#94A3B8]">Loading...</p> : filtered.length === 0 ? <p className="text-sm text-[#94A3B8]">No profiles found</p> : (
        <div className="space-y-2">
          {filtered.map(p => {
            const isEditing = editingId === p.profile_id;
            const isExpanded = expandedId === p.profile_id;
            return (
              <div key={p.profile_id} className={`bg-white rounded-xl shadow-sm overflow-hidden ${!p.active ? 'opacity-50' : ''}`} data-testid={`profile-${p.profile_id}`}>
                {/* Header row */}
                <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => !isEditing && setExpandedId(isExpanded ? null : p.profile_id)}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="p-2 rounded-lg bg-violet-50 flex-shrink-0"><Package className="h-4 w-4 text-violet-600" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-[#0A2540] text-sm truncate">{p.name}</p>
                        <span className="text-[10px] text-[#94A3B8] bg-[#F1F5F9] px-1.5 py-0.5 rounded font-mono">{p.profile_id}</span>
                      </div>
                      <p className="text-[11px] text-[#64748B]">{p.category} | {p.tests_count} tests</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <span className="font-bold text-[#0A2540] text-sm">Rs.{p.selling_price}</span>
                      {p.reference_mrp > p.selling_price && <span className="text-xs text-[#94A3B8] line-through ml-1">Rs.{p.reference_mrp}</span>}
                    </div>
                    {!p.active && <span className="text-[10px] text-red-500 font-bold">INACTIVE</span>}
                    <button onClick={(e) => { e.stopPropagation(); startEdit(p); }} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100" data-testid={`edit-profile-${p.profile_id}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-[#94A3B8]" /> : <ChevronDown className="h-4 w-4 text-[#94A3B8]" />}
                  </div>
                </div>

                {/* Expanded/Edit area */}
                {isExpanded && (
                  <div className="border-t border-[#E2E8F0] p-4">
                    {isEditing ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={e => setForm(p2 => ({ ...p2, name: e.target.value }))} className="h-10 rounded-xl mt-0.5 text-sm" data-testid="edit-profile-name" /></div>
                          <div><Label className="text-xs">Category</Label><Input value={form.category} onChange={e => setForm(p2 => ({ ...p2, category: e.target.value }))} className="h-10 rounded-xl mt-0.5 text-sm" /></div>
                          <div><Label className="text-xs">Selling Price *</Label><Input type="number" value={form.selling_price} onChange={e => setForm(p2 => ({ ...p2, selling_price: e.target.value }))} className="h-10 rounded-xl mt-0.5 text-sm" data-testid="edit-profile-price" /></div>
                          <div><Label className="text-xs">Reference MRP</Label><Input type="number" value={form.reference_mrp} onChange={e => setForm(p2 => ({ ...p2, reference_mrp: e.target.value }))} className="h-10 rounded-xl mt-0.5 text-sm" /></div>
                          <div><Label className="text-xs text-[#94A3B8]">B2B Cost</Label><Input type="number" value={form.b2b_cost} onChange={e => setForm(p2 => ({ ...p2, b2b_cost: e.target.value }))} className="h-10 rounded-xl mt-0.5 text-sm" /></div>
                          <div className="flex items-end pb-1">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={form.active} onChange={e => setForm(p2 => ({ ...p2, active: e.target.checked }))} className="w-4 h-4 rounded" />
                              <span className="text-sm">Active</span>
                            </label>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div><Label className="text-xs">Primary Use</Label><Input value={form.primary_use} onChange={e => setForm(p2 => ({ ...p2, primary_use: e.target.value }))} className="h-10 rounded-xl mt-0.5 text-sm" /></div>
                          <div><Label className="text-xs">Ideal For</Label><Input value={form.ideal_for} onChange={e => setForm(p2 => ({ ...p2, ideal_for: e.target.value }))} className="h-10 rounded-xl mt-0.5 text-sm" /></div>
                          <div><Label className="text-xs">Who Should Take</Label><Input value={form.who_should_take} onChange={e => setForm(p2 => ({ ...p2, who_should_take: e.target.value }))} className="h-10 rounded-xl mt-0.5 text-sm" /></div>
                        </div>
                        <div><Label className="text-xs text-[#94A3B8]">Internal Note</Label><Input value={form.internal_note} onChange={e => setForm(p2 => ({ ...p2, internal_note: e.target.value }))} className="h-10 rounded-xl mt-0.5 text-sm" /></div>

                        {/* Test Management */}
                        <div className="bg-[#F8FAFC] rounded-xl p-4 space-y-3">
                          <p className="text-xs font-bold text-[#0A2540] uppercase tracking-wider">Included Tests ({form.test_codes?.length || 0})</p>
                          <div className="flex flex-wrap gap-1.5">
                            {(form.test_codes || []).map(tc => {
                              const t = allTests.find(x => x.test_code === tc);
                              return (
                                <span key={tc} className="inline-flex items-center gap-1 bg-white border border-[#E2E8F0] rounded-lg px-2 py-1 text-xs">
                                  <span className="font-mono text-[#94A3B8]">{tc}</span>
                                  <span className="text-[#0A2540] font-medium truncate max-w-[150px]">{t?.name || tc}</span>
                                  <button onClick={() => removeTest(tc)} className="text-red-400 hover:text-red-600 ml-0.5"><X className="h-3 w-3" /></button>
                                </span>
                              );
                            })}
                          </div>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#94A3B8]" />
                            <Input value={testSearch} onChange={e => setTestSearch(e.target.value)} placeholder="Search tests to add..." className="pl-9 h-9 rounded-lg text-xs" data-testid="test-search-add" />
                          </div>
                          {testSearch && (
                            <div className="max-h-40 overflow-y-auto space-y-1">
                              {availableTests.filter(t => !form.test_codes?.includes(t.test_code)).slice(0, 10).map(t => (
                                <button key={t.test_code} onClick={() => { addTest(t.test_code); setTestSearch(''); }}
                                  className="w-full flex items-center justify-between p-2 bg-white rounded-lg hover:bg-green-50 text-xs text-left" data-testid={`add-test-${t.test_code}`}>
                                  <span><span className="font-mono text-[#94A3B8] mr-1">{t.test_code}</span> {t.name}</span>
                                  <Plus className="h-3 w-3 text-green-600 flex-shrink-0" />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700 rounded-xl h-10 px-6" data-testid="save-profile-btn">
                            <Check className="h-4 w-4 mr-1" /> Save Changes
                          </Button>
                          <Button onClick={cancelEdit} variant="outline" className="rounded-xl h-10 px-4">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {p.primary_use && <p className="text-sm text-[#475569]"><span className="font-medium text-[#0A2540]">Use:</span> {p.primary_use}</p>}
                        {p.ideal_for && <p className="text-sm text-[#475569]"><span className="font-medium text-[#0A2540]">Ideal for:</span> {p.ideal_for}</p>}
                        {p.who_should_take && <p className="text-sm text-[#475569]"><span className="font-medium text-[#0A2540]">Who should take:</span> {p.who_should_take}</p>}
                        <div>
                          <p className="text-xs font-bold text-[#0A2540] mb-1.5">Tests ({p.tests_count}):</p>
                          <div className="flex flex-wrap gap-1">
                            {(p.test_codes || []).map(tc => {
                              const t = allTests.find(x => x.test_code === tc);
                              return <span key={tc} className="bg-[#F1F5F9] text-[#475569] text-[11px] px-2 py-0.5 rounded-md">{t?.name || tc}</span>;
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HealthProfilesModule;
